import { Controller } from "@hotwired/stimulus"
import { TRIP_MAP_FOCUS_DAY_EVENT, type TripMapFocusDayDetail } from "~/components/TripMap/events"

/**
 * Travel guide page navigation
 * - Highlights the section currently on screen in the sticky "on this page" bar
 * - Keeps the active link visible when the bar scrolls sideways (mobile)
 * - "Show day N on the map" buttons focus the map on that day and scroll to it
 *
 * Usage: data-controller="trip-nav" on the page wrapper
 *        data-trip-nav-target="link" on bar links (href="#section-id")
 *        data-trip-nav-target="section" on sections (sections without a link clear the highlight)
 *        data-trip-nav-target="scroller" on the horizontally scrolling bar
 *        data-action="trip-nav#showOnMap" data-day="2" on "show on map" buttons
 */
export default class extends Controller<HTMLElement> {
  static targets = ["link", "section", "scroller"]

  declare readonly linkTargets: HTMLAnchorElement[]
  declare readonly sectionTargets: HTMLElement[]
  declare readonly scrollerTarget: HTMLElement
  declare readonly hasScrollerTarget: boolean

  private observer: IntersectionObserver | null = null

  connect() {
    // A section is "current" when it crosses a thin band just above the middle of the screen
    this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: "-40% 0px -55% 0px" })
    this.sectionTargets.forEach((section) => this.observer?.observe(section))
    if (this.hasScrollerTarget) {
      this.scrollerTarget.addEventListener("scroll", this.markMore, { passive: true })
      this.markMore()
    }
  }

  disconnect() {
    this.observer?.disconnect()
    this.observer = null
    if (this.hasScrollerTarget) this.scrollerTarget.removeEventListener("scroll", this.markMore)
  }

  // The right edge fades only while there is more of the bar to scroll to
  private markMore = () => {
    const scroller = this.scrollerTarget
    scroller.classList.toggle("has-more", scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 4)
  }

  showOnMap(event: Event) {
    const day = Number((event.currentTarget as HTMLElement).dataset.day)
    const detail: TripMapFocusDayDetail = { day: Number.isFinite(day) ? day : null }

    window.dispatchEvent(new CustomEvent(TRIP_MAP_FOCUS_DAY_EVENT, { detail }))
    document.getElementById("trip-map-root")?.scrollIntoView({ behavior: this.scrollBehavior, block: "center" })
  }

  private onIntersect = (entries: IntersectionObserverEntry[]) => {
    entries
      .filter((entry) => entry.isIntersecting)
      .forEach((entry) => this.activate(entry.target.id))
  }

  private activate(id: string) {
    this.linkTargets.forEach((link) => {
      if (link.getAttribute("href") === `#${id}`) {
        link.setAttribute("aria-current", "true")
        this.reveal(link)
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  private reveal(link: HTMLElement) {
    if (!this.hasScrollerTarget) return

    const scroller = this.scrollerTarget
    const left = link.offsetLeft - (scroller.clientWidth - link.clientWidth) / 2
    scroller.scrollTo({ left, behavior: this.scrollBehavior })
  }

  private get scrollBehavior(): ScrollBehavior {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
  }
}
